import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, Lock, ArrowLeft, RefreshCw, Users, Mail, Calculator,
  Sparkles, Search, CheckCircle2, XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { adminLeads } from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtHu = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch { return iso; }
};

const GOAL_HU = { fat_loss: 'Fogyás', muscle_gain: 'Izomépítés', maintenance: 'Szinten tartás' };

const DRIP_BADGE = {
  active:    { label: 'Drip aktív',    cls: 'bg-sky-500/15 text-sky-300' },
  completed: { label: 'Drip kész',     cls: 'bg-emerald-500/15 text-emerald-300' },
  stopped:   { label: 'Leállítva',     cls: 'bg-gray-600/20 text-gray-400' },
  failed:    { label: 'Hiba',          cls: 'bg-red-500/15 text-red-300' },
};

const DripProgress = ({ drip }) => {
  if (!drip) return <span className="text-xs text-gray-600">Nincs drip</span>;
  const names = ['D0', 'D2', 'D14', 'D21', 'D28', 'D35'];
  const sentSteps = new Set((drip.steps || []).map((s) => s.step));
  return (
    <div className="flex items-center gap-1.5">
      {names.map((n, i) => {
        const sent = sentSteps.has(i);
        return (
          <div key={n} className="flex flex-col items-center" title={n}>
            <span className={`w-2.5 h-2.5 rounded-full ${sent ? 'bg-emerald-400' : 'bg-gray-700'}`} />
            <span className={`text-[9px] mt-0.5 ${sent ? 'text-emerald-400/80' : 'text-gray-600'}`}>{n}</span>
          </div>
        );
      })}
    </div>
  );
};

const LeadCard = ({ lead }) => {
  const badge = lead.drip ? (DRIP_BADGE[lead.drip.status] || DRIP_BADGE.stopped) : null;
  return (
    <div data-testid={`lead-card-${lead.email}`} className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 md:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <a href={`mailto:${lead.email}`} className="font-bold text-white hover:text-yellow-400 transition-colors truncate">
              {lead.email}
            </a>
            {lead.unsubscribed ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-red-500/15 text-red-300">
                <XCircle size={10} /> Leiratkozott
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400/80">
                <CheckCircle2 size={10} /> Feliratkozva
              </span>
            )}
            {badge && (
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${badge.cls}`}>
                {badge.label}
              </span>
            )}
          </div>
          {lead.name && <div className="text-xs text-gray-500 mt-0.5">{lead.name}</div>}
        </div>
        <div className="text-right text-xs text-gray-500">
          Utolsó aktivitás<br /><span className="text-gray-300">{fmtHu(lead.last_activity)}</span>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg bg-gray-950/60 border border-gray-800 p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1">
            <Calculator size={11} /> Makró kalkulátor
          </div>
          {lead.calculator ? (
            <>
              <div className="text-white font-semibold">
                {lead.calculator.target_kcal} kcal cél
                <span className="text-xs text-gray-500 font-normal ml-1.5">
                  ({GOAL_HU[lead.calculator.goal] || lead.calculator.goal})
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Testzsír: {lead.calculator.body_fat_pct}% · TDEE: {lead.calculator.tdee_kcal} kcal
                {lead.calculator.category ? ` · ${lead.calculator.category}` : ''}
              </div>
            </>
          ) : <div className="text-xs text-gray-600">Nem használta</div>}
        </div>

        <div className="rounded-lg bg-gray-950/60 border border-gray-800 p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1">
            <Sparkles size={11} /> Adonis / Vénusz
          </div>
          {lead.aesthetic ? (
            <>
              <div className="text-white font-semibold">
                {lead.aesthetic.score} pont
                <span className="text-xs text-gray-500 font-normal ml-1.5">
                  ({lead.aesthetic.index_type === 'adonis' ? 'Adonis' : 'Vénusz'} · {lead.aesthetic.ratio})
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5 truncate">{lead.aesthetic.category}</div>
            </>
          ) : <div className="text-xs text-gray-600">Nem használta</div>}
        </div>

        <div className="rounded-lg bg-gray-950/60 border border-gray-800 p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 flex items-center gap-1">
            <Mail size={11} /> Drip email sorozat
          </div>
          <DripProgress drip={lead.drip} />
          {lead.drip?.next_send_at && (
            <div className="text-[11px] text-sky-300/80 mt-1.5">
              Következő: {fmtHu(lead.drip.next_send_at)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminLeadsPage = () => {
  useDocumentMeta({ title: 'Leadek | Orosz Coaching Admin' });
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [keyInput, setKeyInput] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      const res = await adminLeads(key);
      setData(res);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Érvénytelen admin kulcs.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else {
        toast.error('Betöltési hiba.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (adminKey) load(adminKey); }, [adminKey, load]);

  const filtered = useMemo(() => {
    const list = data?.leads || [];
    if (!query.trim()) return list;
    const q = query.trim().toLowerCase();
    return list.filter((l) => l.email.includes(q) || (l.name || '').toLowerCase().includes(q));
  }, [data, query]);

  if (!adminKey) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
        <form
          data-testid="admin-leads-login"
          onSubmit={(e) => { e.preventDefault(); if (keyInput.trim()) { localStorage.setItem(ADMIN_KEY_STORAGE, keyInput.trim()); setAdminKey(keyInput.trim()); } }}
          className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl"
        >
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <Users className="text-yellow-500" size={28} />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>Leadek</h1>
          <Input data-testid="admin-leads-key" type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="ADMIN_API_KEY" className="bg-gray-900 border-gray-700 text-white mb-4" autoFocus />
          <Button data-testid="admin-leads-login-btn" type="submit" disabled={!keyInput.trim()} className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
            <Lock className="mr-2" size={16} /> Belépés
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="admin-leads-back" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <div className="w-10 h-10 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <Users className="text-yellow-500" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Leadek</h1>
              <p className="text-xs text-gray-400">
                Kalkulátor-használók, eredményeik és a drip sorozat állapota
                {data ? ` — ${data.total} lead` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input
                data-testid="admin-leads-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Keresés email / név…"
                className="pl-9 h-9 w-56 bg-gray-900 border-gray-700 text-white text-sm"
              />
            </div>
            <Button data-testid="admin-leads-refresh" variant="ghost" size="sm" onClick={() => load(adminKey)} className="text-gray-300 hover:text-white">
              <RefreshCw size={14} />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-24">
            <Loader2 className="animate-spin mx-auto mb-3 text-yellow-500" size={28} />
            <p className="text-gray-400">Leadek betöltése…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-gray-500">Nincs találat.</div>
        ) : (
          <div className="space-y-3 max-w-5xl mx-auto">
            {filtered.map((lead) => <LeadCard key={lead.email} lead={lead} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminLeadsPage;
